import { useEffect, useRef, useState, type CSSProperties } from 'react'
import AuditCtaButton from '@/components/ui/AuditCtaButton'
import { colors } from '@/styles/colors'
import { cmsTextOrFallback } from '@/api/helpers'
import { cmsTextTitle } from '@/styles/cmsText'

interface HeroProps {
  isMobile: boolean
  onVisibilityChange?: (visible: boolean) => void
  /** Hero headline from `GET /api/primary-cta` → `Title` */
  title?: string
  /** When false, omit the static fallback until CMS fetch settles (avoids title flash on refresh). */
  cmsReady?: boolean
  backgroundImage?: string
}

/** Prefer a sentence break so the hero headline stays on two clean lines (Figma). */
function splitHeroTitleIntoLines(title: string): string[] {
  const trimmed = title.trim()
  if (!trimmed) return ['']

  const sentenceBreak = trimmed.match(/^(.+?[.!?])\s+([\s\S]+)$/)
  if (sentenceBreak) {
    return [sentenceBreak[1].trim(), sentenceBreak[2].trim()]
  }

  return [trimmed]
}

function HeroTitle({
  title,
  style,
}: {
  title: string
  style: CSSProperties
}) {
  const lines = splitHeroTitleIntoLines(title)

  return (
    <h1 title={cmsTextTitle(title)} style={style}>
      {lines.map((line, index) => (
        <span key={index} style={{ display: 'block' }}>
          {line}
        </span>
      ))}
    </h1>
  )
}

const DEFAULT_HERO_TITLE = 'Stop Managing Chaos. Start Mastering Growth.'

export default function Hero({
  isMobile,
  onVisibilityChange,
  title,
  cmsReady = true,
  backgroundImage,
}: HeroProps) {
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting
        setIsVisible(visible)
        onVisibilityChange?.(visible)
      },
      { threshold: 0.1 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [onVisibilityChange])

  const heroTitle = cmsTextOrFallback(title, DEFAULT_HERO_TITLE, cmsReady)
  const heroBg = backgroundImage || '/images/martin-martz-R_9BV4WRphI-unsplash 1.png'

  if (isMobile) {
    return (
      <section
        id="home"
        ref={sectionRef}
        aria-label="Hero section"
        style={{
          position: 'relative',
          width: '100vw',
          height: '874px',
          background: '#0B1B25',
          overflow: 'hidden',
        }}
      >
        {/* Background 1: Martin Martz */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundImage: `url("${heroBg}")`,
            backgroundSize: '140% 100%',
            backgroundPosition: 'center',
          }}
        />

        {/* Background 2: Noisy Gradients */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundImage: 'url("/images/noisy-gradients.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply',
          }}
        />

        {/* Overlay */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.01), rgba(0, 0, 0, 0.01)), rgba(0, 0, 0, 0.2)',
            mixBlendMode: 'normal',
          }}
        />

        {/* Floating Icons */}
        {/* Coding */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px',
            gap: '10px',
            position: 'absolute',
            width: '72px',
            height: '72px',
            left: '64px',
            top: '96px',
            background: 'rgba(2, 5, 8, 0.06)',
            borderRadius: '12px',
          }}
        >
          <img
            src="/images/SVGs/Coding.svg"
            alt="Coding"
            style={{ width: '56px', height: '56px' }}
          />
        </div>

        {/* Ai Chipset */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px',
            gap: '10px',
            position: 'absolute',
            width: '72px',
            height: '72px',
            left: '289px',
            top: '180px',
            background: 'rgba(2, 5, 8, 0.06)',
            borderRadius: '12px',
            
          }}
        >
          <img
            src="/images/SVGs/Ai Chipset.svg"
            alt="Ai Chipset"
            style={{ width: '56px', height: '56px' , filter: 'brightness(0) invert(1)'}}
      
          />
        </div>

        {/* Figma */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px',
            gap: '10px',
            position: 'absolute',
            width: '72px',
            height: '72px',
            left: '19px',
            top: '500px',
            background: 'rgba(2, 5, 8, 0.06)',
            borderRadius: '12px',
            zIndex: 5,
          }}
        >
          <img
            src="/images/SVGs/Figma.svg"
            alt="Figma"
            style={{ width: '48px', height: '48px' }}
          />
        </div>

        {/* Github */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px',
            gap: '10px',
            position: 'absolute',
            width: '72px',
            height: '72px',
            left: '286px',
            top: '560px',
            background: 'rgba(2, 5, 8, 0.06)',
            borderRadius: '12px',
            zIndex: 5,
          }}
        >
          <img
            src="/images/SVGs/github.svg"
            alt="Github"
            style={{ width: '56px', height: '56px' }}
          />
        </div>

        {/* Hero Title */}
        <HeroTitle
          title={heroTitle}
          style={{
            position: 'absolute',
            width: '347px',
            left: 'calc(50% - 347px/2 - 0.5px)',
            top: '280px',
            fontFamily: "'Instrument Sans', sans-serif",
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '32px',
            lineHeight: '39px',
            textAlign: 'center',
            color: '#F4F0EF',
            margin: 0,
            zIndex: 1,
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          }}
        />

        <AuditCtaButton
          href="#contact"
          variant="light"
          compact
          style={{
            position: 'absolute',
            left: '50%',
            top: '420px',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        >
          Get your Free Audit
        </AuditCtaButton>

        {/* Scroll Indicator */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0px',
            gap: '2px',
            position: 'absolute',
            width: '136px',
            height: '66px',
            left: 'calc(50% - 136px/2)',
            top: 'calc(50% - 66px/2 + 270px)',
            zIndex: 20,
          }}
        >
          {/* Arrow Down Icon */}
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16 26L16 6M16 26L7 17M16 26L25 17"
              stroke="#FFFFFF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            style={{
              width: '136px',
              height: '32px',
              fontFamily: "'Instrument Sans', sans-serif",
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '130%',
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
              justifyContent: 'center',
            }}
          >
            Scroll for more
          </span>
        </div>

        {/* Bottom gradient to blend into next section */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '520px',
            background:
              'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,1) 100%)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      </section>
    )
  }

  return (
    <section
      id="home"
      ref={sectionRef}
      aria-label="Hero section"
      style={{
        position: 'relative',
        width: '1920px',
        height: '944px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: colors.background.dark,
        overflow: 'hidden',
      }}
    >
      {/* Background Image */}
      <div
        style={{
          position: 'absolute',
          width: '1920px',
          height: '944px',
          left: '0px',
          top: '0px',
          pointerEvents: 'none',
        }}
      >
        <img
          src={heroBg}
          alt="Background"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>

      {/* Noisy Gradients Overlay */}
      <div
        style={{
          position: 'absolute',
          width: '1920px',
          height: '944px',
          left: '0px',
          top: '0px',
          pointerEvents: 'none',
        }}
      >
        <img
          src="/images/noisy-gradients.png"
          alt="Noise Overlay"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            mixBlendMode: 'multiply',
            opacity: 1,
          }}
        />
      </div>

      {/* Additional Dark Overlay */}
      <div
        style={{
          position: 'absolute',
          width: '1920px',
          height: '944px',
          left: '0px',
          top: '0px',
          background: 'rgba(0, 0, 0, 0.15)',
          pointerEvents: 'none',
        }}
      />

      {/* Floating Icons */}
      <div
        style={{
          position: 'absolute',
          width: '88px',
          height: '88px',
          left: '366px',
          top: '228px',
          background: 'rgba(2, 5, 8, 0.06)',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px',
          pointerEvents: 'none',
          zIndex: 5,
        }}
      >
        <img src="/images/SVGs/Figma.svg" alt="Figma" width={48} height={48} />
      </div>

      <div
        style={{
          position: 'absolute',
          width: '88px',
          height: '88px',
          left: '1369px',
          top: '176px',
          background: 'rgba(2, 5, 8, 0.06)',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px',
          pointerEvents: 'none',
        }}
      >
        <img src="/images/SVGs/Coding.svg" alt="Coding" width={56} height={56}  />
      </div>

      <div
        style={{
          position: 'absolute',
          width: '88px',
          height: '88px',
          left: '1631px',
          top: '443px',
          background: 'rgba(2, 5, 8, 0.06)',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px',
          pointerEvents: 'none',
        }}
      >
        <img src="/images/design.png" alt="Design" width={48} height={48} />
      </div>

      <div
        style={{
          position: 'absolute',
          width: '88px',
          height: '88px',
          left: '180px',
          top: '492px',
          background: 'rgba(2, 5, 8, 0.06)',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px',
          pointerEvents: 'none',
        }}
      >
        <img
          src="/images/SVGs/Ai Chipset.svg"
          alt="AI Chipset"
          width={56}
          height={56}
          style={{ filter: 'brightness(0) invert(1)' }}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          width: '88px',
          height: '88px',
          left: '520px',
          top: '635px',
          background: 'rgba(2, 5, 8, 0.06)',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px',
          pointerEvents: 'none',
          zIndex: 5,
        }}
      >
        <img src="/images/SVGs/github.svg" alt="GitHub" width={56} height={56} />
      </div>

      <div
        style={{
          position: 'absolute',
          width: '88px',
          height: '88px',
          left: '1389px',
          top: '619px',
          background: 'rgba(2, 5, 8, 0.06)',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px',
        }}
      >
        <img src="/images/SVGs/Visual studio code.svg" alt="VS Code" width={48} height={48} />
      </div>

      {/* Hero Content */}
      <div
        className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        style={{
          position: 'absolute',
          width: '1384px',
          height: '186px',
          left: '268px',
          top: '333px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <HeroTitle
          title={heroTitle}
          style={{
            width: '946px',
            maxWidth: '100%',
            fontFamily: 'var(--font-instrument-sans)',
            fontWeight: 400,
            fontSize: '76px',
            lineHeight: '93px',
            textAlign: 'center',
            color: colors.neutral[95],
            margin: 0,
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          }}
        />
      </div>

      <div
        className={isVisible ? 'opacity-100' : 'opacity-0'}
        style={{
          position: 'absolute',
          left: '50%',
          top: '574px',
          transform: isVisible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(10px)',
          zIndex: 10,
          transition:
            'opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
          transitionDelay: '100ms',
        }}
      >
        <AuditCtaButton href="#contact" variant="light">
          Get your Free Audit
        </AuditCtaButton>
      </div>

      {/* Bottom gradient to blend into next section */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '520px',
          background:
            'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,1) 100%)',
          pointerEvents: 'none',
        }}
      />
    </section>
  )
}

